import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { HowToBody } from "@/components/HowToBody";
import { BookOpen, ShieldCheck, Lock, ArrowDown } from "lucide-react";
import { toast } from "sonner";

const AGE_KEY = "playden_age_agreed_v1";
const HOWTO_KEY = "playden_howto_agreed_v1";

const ageOk = () => localStorage.getItem(AGE_KEY) === "true";
const howtoOk = () => localStorage.getItem(HOWTO_KEY) === "true";

// Step 2 of onboarding: after the Age Gate, the player must read the How-To and
// agree to the rules at the very bottom. Accepting unlocks the full site.
export function HowToGate() {
  const [show, setShow] = useState(() => ageOk() && !howtoOk());

  // Re-evaluate when the Age Gate advances, and on storage changes (other tabs).
  useEffect(() => {
    const reeval = () => setShow(ageOk() && !howtoOk());
    window.addEventListener("playden:gate-advance", reeval);
    window.addEventListener("storage", reeval);
    return () => {
      window.removeEventListener("playden:gate-advance", reeval);
      window.removeEventListener("storage", reeval);
    };
  }, []);

  useEffect(() => {
    if (show) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [show]);

  if (!show) return null;

  const agree = () => {
    localStorage.setItem(HOWTO_KEY, "true");
    localStorage.setItem(HOWTO_KEY + "_at", new Date().toISOString());
    setShow(false);
    toast.success("Welcome to Playden — you're all set.");
  };
  const decline = () => {
    try { window.location.href = "https://www.google.com"; } catch (_) { /* noop */ }
  };

  return (
    <div
      data-testid="howto-gate"
      className="fixed inset-0 z-[100] overflow-y-auto"
      style={{ backgroundColor: "rgba(4, 6, 10, 0.94)", backdropFilter: "blur(10px)" }}
      onContextMenu={(e) => e.preventDefault()}
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Header */}
        <div className="relative rounded-2xl border border-white/10 bg-[hsl(222_22%_8%)] p-6 sm:p-8 overflow-hidden mb-6">
          <div className="absolute inset-0 holo-scan opacity-10 pointer-events-none" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.35em] text-amber-300/90 mb-3">
              <BookOpen className="h-3.5 w-3.5" /> Step 2 of 2 · Player Onboarding
            </div>
            <h1 className="font-display text-4xl sm:text-5xl text-white tracking-[0.04em] leading-[0.95]">
              HOW TO PLAY<span className="text-amber-300"> &amp; THE RULES</span>
            </h1>
            <p className="text-white/60 mt-3 max-w-2xl text-sm sm:text-base leading-relaxed">
              You're almost in. Read how Playden works below — collecting, top-ups, battles, trading and more — then agree to the rules at the bottom to unlock the full site.
            </p>
            <div className="mt-4 inline-flex items-center gap-2 text-amber-200/80 text-xs font-mono">
              <ArrowDown className="h-3.5 w-3.5 animate-bounce" /> Scroll down — the “I agree” button is at the very bottom.
            </div>
          </div>
        </div>

        {/* The how-to content (links are inert here so nothing slips past the gate) */}
        <HowToBody linksClickable={false} />

        {/* Agreement — at the bottom of the how-to, as requested */}
        <div data-testid="howto-agree-bar" className="mt-8 rounded-2xl border border-amber-400/30 bg-amber-400/[0.06] p-6 sm:p-8">
          <div className="flex items-start gap-3">
            <div className="h-11 w-11 rounded-xl bg-amber-400/15 border border-amber-300/40 flex items-center justify-center shrink-0">
              <ShieldCheck className="h-6 w-6 text-amber-300" />
            </div>
            <div>
              <h3 className="font-display text-2xl text-white tracking-[0.04em]">AGREE TO THE RULES &amp; HOW TO PLAY</h3>
              <p className="text-white/60 text-sm mt-1 max-w-2xl leading-relaxed">
                By selecting <span className="text-white font-medium">“I agree”</span> you confirm you've read this guide and accept Playden's rules: you're 21+, all purchases and Challenge Entries are final and non-refundable, Community Prediction Pools are matched between players (never against the house), and you'll play fairly and lawfully. You accept sole responsibility for your conduct and indemnify Playden and its operators.
              </p>
            </div>
          </div>
          <div className="mt-5 flex flex-col-reverse sm:flex-row gap-3">
            <Button
              data-testid="howto-decline"
              variant="outline"
              onClick={decline}
              className="sm:flex-1 border-white/15 text-white/70 hover:bg-white/5"
            >
              <Lock className="h-4 w-4 mr-1.5" /> I don't agree — leave
            </Button>
            <Button
              data-testid="howto-agree"
              onClick={agree}
              className="sm:flex-[2] bg-amber-400 text-black hover:bg-amber-300 font-medium text-base h-12"
            >
              <ShieldCheck className="h-5 w-5 mr-2" /> I agree — unlock Playden
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
